import { 
    Message, 
    FormattedRequestObject,
    ContentFile,
    User
} from "@/lib/types"
import { models, ModelId } from "@/lib/models/model-list"
import { toast } from "sonner"
import { buildSystemPrompt } from "./system-prompt"

// Import AI SDK message types
import { ImagePart, ModelMessage, TextPart, UserModelMessage } from 'ai'



// Construct an array of past messages to be sent to the api based of a parentId
const buildMessageHistory = (parentId: string, messages: Message[]) => {
    const messageHistory: Message[] = []
    function findAncestor(parentId: string | null) {
        const currentMessage = messages.find((msg) => msg.id === parentId)
        if (currentMessage) {
            if (currentMessage.info.type !== 'dropdown') {
                messageHistory.push(currentMessage)
            }
            findAncestor(currentMessage.parentId)
        }
    }
    findAncestor(parentId)

    // Reverse the message history to get chronological order
    // Filter out messages with errors but keep subsequent valid messages
    const finalMessageHistory = []
    
    let skipNext = false
    for (let i = 0; i < messageHistory.length; i++) {
        const message = messageHistory[i]
        
        if (skipNext) {
            skipNext = false
            continue
        }
        
        // Skip this message if it has errors and mark to skip the next one
        if (message.error) {
            skipNext = true
            continue
        }
        
        finalMessageHistory.push(message)
    }
    
    return finalMessageHistory.reverse()
}

// Merge consecutive user messages so the model always sees alternating roles
// This **should** only happens when there is an error on an assistant message, but then the user message is still part of the conversation and the user sends another message.
// this should actualy never happen as the user or assistant message aren't being logged on reqeust error, the user message contnet is added back to the prompt and the draft 
// just li
// function mergeConsecutiveUsers(history: Message[]): Message[] {
//     const merged: Message[] = []
//     for (const msg of history) {
//         const last = merged[merged.length - 1]
//         if (last && last.role === 'user' && msg.role === 'user') {
//             merged[merged.length - 1] = {
//                 ...msg,
//                 content: [last.content, msg.content].filter(Boolean).join('\n\n'),
//                 files: [...(last.files || []), ...(msg.files || [])],
//                 parentId: last.parentId
//             }
//         } else {
//             merged.push(msg)
//         }
//     }
//     return merged
// }

// Typed helpers for input objects
type StreamTextInputObject = Extract<FormattedRequestObject['inputObject'], { mode: 'stream-text' }>
type GenerateImageInputObject = Extract<FormattedRequestObject['inputObject'], { mode: 'generate-image' }>

function formatStreamTextInputObject({
    userMessage,
    assistantMessage,
    messages,
    conversationSystemPrompt,
    keepFilesInRequest,
    user,
    userId
}: {
    userMessage: Message,
    assistantMessage: Message,
    messages: Message[],
    conversationSystemPrompt?: string,
    keepFilesInRequest: boolean,
    user: User,
    userId: string
}): StreamTextInputObject {
    const messageHistory = buildMessageHistory(userMessage.parentId as string, messages)
    messageHistory.push(userMessage)

    const systemPrompt = buildSystemPrompt({ user, conversationSystemPrompt })
    const systemMessage: ModelMessage = {
        role: 'system',
        content: systemPrompt
    }

    // Convert messages to Vercel AI SDK format
    const formattedMessages: ModelMessage[] = messageHistory.map((msg, index) => {
        // Base message structure with initial content as empty array
        const formattedMessage: ModelMessage = {
            role: msg.role,
            content: []
        }

        // Create content array
        const contentArray: Array<TextPart | ImagePart> = []
        let textPart: TextPart | undefined

        // Add text content
        if (msg.content) {
            textPart = {
                type: 'text',
                text: msg.content
            }
            contentArray.push(textPart)
        }

        // console.log('format request', msg.id.slice(0, 5), msg.files.length)

        // Add file content if present and either keepFilesInRequest is true or it's the last message
        if (msg.files && msg.files.length > 0 && (keepFilesInRequest || index === messageHistory.length - 1)) {
            let fileTextPart = '<files_list>\n'
            
            msg.files.forEach((file: ContentFile) => {
                if (file.mimeType && file.mimeType.includes("image") && file.url) {
                    try {
                        const imageUrl = new URL(file.url)
                        const imagePart: ImagePart = {
                            type: 'image',
                            image: imageUrl
                        }
                        contentArray.push(imagePart)
                    } catch {
                        // Ignore invalid URL; fallback to textual representation below
                    }
                }
                
                if (file.content) {
                    fileTextPart += `  <file>\n`
                    fileTextPart += `    <name>${file.name}</name>\n`
                    fileTextPart += `    <extension>${file.extension}</extension>\n`
                    fileTextPart += `    <mime_type>${file.mimeType}</mime_type>\n`
                    fileTextPart += `    <content>\n${file.content && file.content ? file.content : file.url}\n    </content>\n`
                    fileTextPart += `  </file>\n`
                }
            })
            
            fileTextPart += '</files_list>'
            
            // Only add the file text part if there's actual content
            if (fileTextPart !== '<files_list>\n</files_list>') {
                if (textPart) {
                    textPart.text += '\n\n' + fileTextPart
                } else {
                    textPart = {
                        type: 'text',
                        text: fileTextPart
                    }
                    contentArray.push(textPart)
                }
            }
        }

        // If it's just text content, use a string
        if (contentArray.length === 1 && contentArray[0].type === 'text') {
            formattedMessage.content = contentArray[0].text
        } else {
            // For mixed content (text + images), use the array format
            formattedMessage.content = contentArray
        }

        // For assistant role, content must be string only
        if (msg.role === 'assistant' && typeof formattedMessage.content !== 'string') {
            formattedMessage.role = 'user'
        }

        return formattedMessage
    })
    
    const allMessages = [systemMessage, ...formattedMessages]
    
    return {
        mode: 'stream-text',
        messages: allMessages,
        modelId: assistantMessage.modelId,
        urlTracking: window.location.pathname.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, '') || '',
        userId: userId,
    }
}

function formatGenerateImageInputObject({
    userMessage,
    assistantMessage,
    userId
}: {
    userMessage: Message,
    assistantMessage: Message,
    userId: string
}): GenerateImageInputObject {
    const sourceImageFile = (userMessage.files || []).find(f => (f.mimeType || '').startsWith('image') && !!f.url)
    return {
        mode: 'generate-image',
        messages: [],
        modelId: assistantMessage.modelId,
        urlTracking: window.location.pathname.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, '') || '',
        userId: userId,
        prompt: (userMessage.content || '').toString().trim() || '',
        imageUrl: sourceImageFile?.url
    }
}

export const formatRequestObject = ({
    userMessage,
    assistantMessage,
    messages,
    conversationSystemPrompt,
    conversationId,
    keepFilesInRequest,
    userId,
    clientData = {}, 
    user
}: {
    userMessage: Message,
    assistantMessage: Message,
    messages: Message[],
    conversationSystemPrompt?: string,
    conversationId: string,
    keepFilesInRequest: boolean,
    userId: string,
    clientData: FormattedRequestObject['clientData'],
    user: User
}): FormattedRequestObject => {
    // If selected model outputs images, use a minimal image payload (no history, no system prompt)
    const selectedModel = models[assistantMessage.modelId]
    const isImageModel = Array.isArray(selectedModel?.architecture?.outputModalities)
        && selectedModel.architecture.outputModalities.includes('image')

    const inputObject = isImageModel
        ? formatGenerateImageInputObject({ userMessage, assistantMessage, userId })
        : formatStreamTextInputObject({ userMessage, assistantMessage, messages, conversationSystemPrompt, keepFilesInRequest, user, userId })

    const returnObject: FormattedRequestObject = {
        assistantMessage: assistantMessage,
        userMessage,
        conversationId: conversationId,
        inputObject,
        timeLogs: userMessage.optional_data_for_dev?.logs || [],
        clientData: {
            fingerprint: generateFingerprint(),
            ...clientData
        }
    }

    console.log('returnObject in formatRequestObject', returnObject)
    return returnObject
}



// Simple function to generate a basic fingerprint
function generateFingerprint(): string {
    const userAgent = navigator.userAgent
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const screenResolution = `${window.screen.width}x${window.screen.height}`
    const colorDepth = window.screen.colorDepth
    const language = navigator.language

    return `${userAgent}|${timeZone}|${screenResolution}|${colorDepth}|${language}`
}